import { observable, action, computed } from 'mobx';
import { StoresApiSdk } from '~api/StoresApiSdk';
import { ICountryResult, IStoresDirectoryItem } from '~api/Interfaces';
import { StoresDirectoryItem } from '~domain/entities/StoresDirectoryItem';

export interface IStoresDirectoryGroup {
    stateAbbr: string | null;
    stores: StoresDirectoryItem[];
}

export interface IStoresDirectoryError {
    status: number;
    detail: string;
}

export class StoresDirectoryRepository {
    private storesApi: StoresApiSdk;

    @observable public stores: IStoresDirectoryGroup[] = [];
    @observable public countries: ICountryResult[] = [];
    @observable private error: IStoresDirectoryError;

    constructor(storesApi: StoresApiSdk) {
        this.storesApi = storesApi;
    }

    private storesDirectoryWithStates = (items: IStoresDirectoryItem[]) => {
        const reducedStoreDirGroups = items.reduce(
            (storeDirGroups: IStoresDirectoryGroup[], currentItem) => {
                const foundIndex = storeDirGroups.findIndex(
                    (group) => group.stateAbbr === currentItem.address.state
                );
                if (foundIndex !== -1) {
                    storeDirGroups[foundIndex].stores.push(
                        StoresDirectoryItem.fromJson(currentItem)
                    );
                } else {
                    storeDirGroups.push({
                        stateAbbr: currentItem.address.state,
                        stores: [StoresDirectoryItem.fromJson(currentItem)]
                    });
                }

                return storeDirGroups;
            },
            []
        );
        this.updateStores(reducedStoreDirGroups);
    };

    private storesDirectoryWithoutStates = (items: IStoresDirectoryItem[]) => {
        const storesDirGroup: IStoresDirectoryGroup = {
            stateAbbr: null,
            stores: items.map((data) => StoresDirectoryItem.fromJson(data))
        };
        this.updateStores([storesDirGroup]);
    };

    public loadStoresDirectory = async () => {
        try {
            const response = await this.storesApi.getStoresDirectory();
            if (response) {
                if (response.storesDirectory.showState) {
                    this.storesDirectoryWithStates(response.storesDirectory.items);
                } else {
                    this.storesDirectoryWithoutStates(response.storesDirectory.items);
                }
            }
        } catch (err) {
            this.updateStores([]);
            this.setStoresDirectoryError(err);
        }
    };

    @action
    private setStoresDirectoryError(error: IStoresDirectoryError) {
        this.error = error;
    }

    @computed
    public get storesDirectoryError(): IStoresDirectoryError {
        return this.error;
    }

    @action
    public updateStores = (stores: IStoresDirectoryGroup[]) => {
        this.stores = stores;
    };
}
